import {request} from './client';
import type {
	ProjectResponse,
	ProjectRequest,
	DiagramResponse,
	DiagramPageResponse,
	DiagramRequest,
	DiagramType,
	ShareLinkResponse,
	ShareInfoResponse,
	ModelPatchDto,
	CatalogSearchResponse,
	CatalogPackageNode,
	CatalogRelationshipHit,
	WorkspaceTreeNode,
	WorkspaceSearchHit,
	WorkspaceSearchPage,
	FolderRequest,
	FolderResponse,
	CreateShareLinkRequest,
	ProjectMemberResponse,
	UpdateMemberRoleRequest,
	AcceptShareResponse,
} from '@/types/api';

export type {
	ProjectResponse,
	ProjectRequest,
	DiagramResponse,
	DiagramPageResponse,
	DiagramRequest,
	DiagramType,
	ShareLinkResponse,
	ShareInfoResponse,
	WorkspaceTreeNode,
	WorkspaceSearchHit,
	WorkspaceSearchPage,
	FolderRequest,
	FolderResponse,
	CreateShareLinkRequest,
	ProjectMemberResponse,
	UpdateMemberRoleRequest,
	AcceptShareResponse,
};

export const projectsApi = {
	async list() {
		return request<ProjectResponse[]>('/api/projects', {method: 'GET'});
	},
	async get(id: number) {
		return request<ProjectResponse>(`/api/projects/${id}`, {method: 'GET'});
	},
	async getShareInfo(token: string) {
		return request<ShareInfoResponse>(`/api/share/${encodeURIComponent(token)}`, {
			method: 'GET',
		});
	},
	async acceptShare(token: string) {
		return request<AcceptShareResponse>(`/api/share/${encodeURIComponent(token)}/accept`, {
			method: 'POST',
		});
	},
	async listMembers(projectId: number) {
		return request<ProjectMemberResponse[]>(`/api/projects/${projectId}/members`, {
			method: 'GET',
		});
	},
	async updateMemberRole(projectId: number, userId: number, payload: UpdateMemberRoleRequest) {
		return request<ProjectMemberResponse>(`/api/projects/${projectId}/members/${userId}`, {
			method: 'PATCH',
			body: payload,
		});
	},
	async removeMember(projectId: number, userId: number) {
		return request<undefined>(`/api/projects/${projectId}/members/${userId}`, {
			method: 'DELETE',
		});
	},
	async listShareLinks(projectId: number) {
		return request<ShareLinkResponse[]>(`/api/projects/${projectId}/share-links`, {
			method: 'GET',
		});
	},
	async createShareLink(projectId: number, payload: CreateShareLinkRequest) {
		return request<ShareLinkResponse>(`/api/projects/${projectId}/share-links`, {
			method: 'POST',
			body: payload,
		});
	},
	async revokeShareLink(projectId: number, linkId: number) {
		return request<undefined>(`/api/projects/${projectId}/share-links/${linkId}`, {
			method: 'DELETE',
		});
	},
	async create(payload: ProjectRequest) {
		return request<ProjectResponse>('/api/projects', {method: 'POST', body: payload});
	},
	async update(id: number, payload: ProjectRequest) {
		return request<ProjectResponse>(`/api/projects/${id}`, {method: 'PUT', body: payload});
	},
	async delete(id: number) {
		return request<undefined>(`/api/projects/${id}`, {method: 'DELETE'});
	},
	async searchCatalog(
		projectId: number,
		params: {
			q?: string;
			type?: string;
			packageId?: string;
			diagramId?: number;
			limit?: number;
			offset?: number;
		} = {}
	) {
		const query = new URLSearchParams();
		if (params.q?.trim()) query.set('q', params.q.trim());
		if (params.type?.trim()) query.set('type', params.type.trim());
		if (params.packageId?.trim()) query.set('packageId', params.packageId.trim());
		if (params.diagramId != null) query.set('diagramId', String(params.diagramId));
		if (params.limit != null) query.set('limit', String(params.limit));
		if (params.offset != null) query.set('offset', String(params.offset));
		const qs = query.toString();
		const path = `/api/projects/${projectId}/catalog/elements${qs ? `?${qs}` : ''}`;
		return request<CatalogSearchResponse>(path, {method: 'GET'});
	},
	async catalogPackages(projectId: number) {
		return request<CatalogPackageNode[]>(`/api/projects/${projectId}/catalog/packages`, {
			method: 'GET',
		});
	},
	async searchCatalogRelationships(projectId: number, params: {ends: string[]}) {
		const ends = (params.ends ?? []).filter(id => typeof id === 'string' && id.trim());
		return request<CatalogRelationshipHit[]>(`/api/projects/${projectId}/catalog/relationships`, {
			method: 'POST',
			body: {ends},
		});
	},
};

export const diagramsApi = {
	async list(
		projectId: number,
		params: {
			q?: string;
			limit?: number;
			offset?: number;
		} = {}
	) {
		const query = new URLSearchParams();
		if (params.q?.trim()) query.set('q', params.q.trim());
		if (params.limit != null) query.set('limit', String(params.limit));
		if (params.offset != null) query.set('offset', String(params.offset));
		const qs = query.toString();
		const path = `/api/projects/${projectId}/diagrams${qs ? `?${qs}` : ''}`;
		return request<DiagramPageResponse>(path, {method: 'GET'});
	},
	async get(projectId: number, diagramId: number) {
		return request<DiagramResponse>(`/api/projects/${projectId}/diagrams/${diagramId}`, {
			method: 'GET',
		});
	},
	async create(projectId: number, payload: DiagramRequest) {
		return request<DiagramResponse>(`/api/projects/${projectId}/diagrams`, {
			method: 'POST',
			body: payload,
		});
	},
	async update(projectId: number, diagramId: number, payload: {name?: string}) {
		return request<DiagramResponse>(`/api/projects/${projectId}/diagrams/${diagramId}`, {
			method: 'PUT',
			body: payload,
		});
	},
	async move(projectId: number, diagramId: number, parentFolderId: number | null) {
		return request<DiagramResponse>(`/api/projects/${projectId}/diagrams/${diagramId}/move`, {
			method: 'PUT',
			body: {parentFolderId},
		});
	},
	/** Применить макро-патч к модели (инкрементальное обновление) */
	async applyPatches(projectId: number, diagramId: number, patch: ModelPatchDto) {
		return request<DiagramResponse>(`/api/projects/${projectId}/diagrams/${diagramId}/model`, {
			method: 'PATCH',
			body: patch,
		});
	},
	async undoModel(
		projectId: number,
		diagramId: number,
		body: {expectedStructuralVersion: number; expectedHistoryStackCursor?: number}
	) {
		return request<DiagramResponse>(
			`/api/projects/${projectId}/diagrams/${diagramId}/model/undo`,
			{method: 'POST', body}
		);
	},
	async redoModel(
		projectId: number,
		diagramId: number,
		body: {expectedStructuralVersion: number; expectedHistoryStackCursor?: number}
	) {
		return request<DiagramResponse>(
			`/api/projects/${projectId}/diagrams/${diagramId}/model/redo`,
			{method: 'POST', body}
		);
	},
	/** Сохраняет модель: content — JSON строка (приоритет) или XML (legacy) */
	async updateContent(projectId: number, diagramId: number, content: string | object) {
		const payload = typeof content === 'string' ? content : JSON.stringify(content);
		return request<DiagramResponse>(`/api/projects/${projectId}/diagrams/${diagramId}/content`, {
			method: 'PUT',
			body: {content: payload},
		});
	},
	async delete(projectId: number, diagramId: number) {
		return request<undefined>(`/api/projects/${projectId}/diagrams/${diagramId}`, {
			method: 'DELETE',
		});
	},
	/** Экспорт диаграммы в XML (из content или из таблиц) */
	async exportXml(projectId: number, diagramId: number) {
		const API_BASE =
			typeof process !== 'undefined' && process.env?.REACT_APP_API_URL !== undefined
				? process.env.REACT_APP_API_URL
				: '';
		const res = await fetch(
			`${API_BASE}/api/projects/${projectId}/diagrams/${diagramId}/export`,
			{method: 'GET', credentials: 'include'}
		);
		if (!res.ok)
			return {
				data: null as string | null,
				error: {message: res.statusText},
				status: res.status,
			};
		const text = await res.text();
		return {data: text || null, error: undefined, status: res.status};
	},
};

export const workspaceApi = {
	async search(q: string, offset = 0, limit = 20) {
		const query = new URLSearchParams();
		if (q.trim()) query.set('q', q.trim());
		query.set('offset', String(offset));
		query.set('limit', String(limit));
		return request<WorkspaceSearchPage>(`/api/workspace/search?${query.toString()}`, {method: 'GET'});
	},
};

export const foldersApi = {
	async tree(projectId: number, parentFolderId?: number | null) {
		const query = new URLSearchParams();
		if (parentFolderId != null) query.set('parentFolderId', String(parentFolderId));
		const qs = query.toString();
		const path = `/api/projects/${projectId}/tree${qs ? `?${qs}` : ''}`;
		return request<WorkspaceTreeNode[]>(path, {method: 'GET'});
	},
	async create(projectId: number, payload: FolderRequest) {
		return request<FolderResponse>(`/api/projects/${projectId}/folders`, {
			method: 'POST',
			body: payload,
		});
	},
	async update(projectId: number, folderId: number, payload: FolderRequest) {
		return request<FolderResponse>(`/api/projects/${projectId}/folders/${folderId}`, {
			method: 'PUT',
			body: payload,
		});
	},
	async delete(projectId: number, folderId: number) {
		return request<undefined>(`/api/projects/${projectId}/folders/${folderId}`, {
			method: 'DELETE',
		});
	},
	async move(projectId: number, folderId: number, parentFolderId: number | null) {
		return request<FolderResponse>(`/api/projects/${projectId}/folders/${folderId}/move`, {
			method: 'PUT',
			body: {parentFolderId},
		});
	},
};
